import { ref, computed, onUnmounted } from 'vue';
import FetchService from '@/services/fetch.service';

export function useSyncStatus(integrationType: string) {
  // Centralized state
  const syncLogs = ref<any[]>([]);
  const syncing = ref(false);
  const syncInterval = ref<NodeJS.Timeout | null>(null);
  
  // Computed properties
  const isSyncCompleted = computed(() => {
    if (!syncLogs.value || syncLogs.value.length === 0) {
      return true;
    }
    // Return true only when there are no active/running syncs
    return !hasActiveSync.value;
  });

  const hasActiveSync = computed(() => {
    return syncLogs.value.some(entry => 
      entry.status?.toLowerCase() === 'running'
    );
  });

  // Methods
  const startSync = async (entities: string[]) => {
    if (!entities.length) {
      throw new Error('Please select at least one entity to sync.');
    }

    syncing.value = true;
    
    try {
      const queryParams = entities
        .map((entity) => `entities[]=${entity}`)
        .join('&');
      
      let endpoint = '';
      if (integrationType === 'facts') {
        endpoint = `/api-sync/facts/sync?${queryParams}`;
      } else if (integrationType === 'nycdoe') {
        endpoint = `/api-sync/oneroster/sync?${queryParams}`;
      } else {
        endpoint = `/api-sync/oneroster/sync?${queryParams}`;
      }
      
      const response = await FetchService.fetchData(endpoint, 'get');
      
      // Start polling for sync status
      pollSyncStatus();
      
      return {
        message: response.message || 'Sync started successfully! This may take a few minutes to complete.',
        color: 'success'
      };
    } catch (error) {
      console.error('Error starting sync:', error);
      syncing.value = false;
      throw new Error('Error starting sync. Please check your configuration and try again.');
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const endpoint = integrationType === 'facts' ? '/api-sync/facts/status' : '/api-sync/oneroster/status';
      const response = await FetchService.fetchData(endpoint, 'get');
      syncLogs.value = Array.isArray(response) ? response : [];

      if (isSyncCompleted.value) {
        syncing.value = false;
        if (syncInterval.value) {
          clearInterval(syncInterval.value);
          syncInterval.value = null;
        }
      } else if (hasActiveSync.value) {
        syncing.value = true;
        // Only start polling if not already polling
        if (!syncInterval.value) {
          pollSyncStatus();
        }
      }
    } catch (error) {
      console.error('Error fetching sync logs:', error);
      syncLogs.value = [];
    }
  };

  const pollSyncStatus = () => {
    // Clear any existing interval first
    if (syncInterval.value) {
      clearInterval(syncInterval.value);
      syncInterval.value = null;
    }

    syncInterval.value = setInterval(async () => {
      try {
        await fetchSyncStatus();

        if (isSyncCompleted.value) {
          clearInterval(syncInterval.value!);
          syncInterval.value = null;
          syncing.value = false;
        }
      } catch (error) {
        console.error('Error polling sync status:', error);
        if (syncInterval.value) {
          clearInterval(syncInterval.value);
          syncInterval.value = null;
        }
        syncing.value = false;
      }
    }, 15000); // Poll every 15 seconds
  };

  const stopPolling = () => {
    if (syncInterval.value) {
      clearInterval(syncInterval.value);
      syncInterval.value = null;
    }
  };

  // Cleanup on unmount
  onUnmounted(() => {
    stopPolling();
  });

  return {
    // State
    syncLogs,
    syncing,
    
    // Computed
    isSyncCompleted,
    hasActiveSync,
    
    // Methods
    startSync,
    fetchSyncStatus,
    stopPolling
  };
}
